import React, { useState, useEffect, useMemo } from "react";
import {
  CheckCircle2, Clock, Calendar, Link2, Rocket, ShieldCheck,
  ChevronRight, ExternalLink, Layers, Target, Bell, UserCheck, RotateCcw
} from "lucide-react";
import { SeoMarket, SeoOverview, PusherState } from "./types";
import { seoApi } from "../../services/seoApi";

interface SeoRoadmapTabProps {
  market: SeoMarket;
  overview: SeoOverview | null;
  pusherState: PusherState | null;
}

type TrackFilter = "all" | "pushes" | "backlinks" | "content" | "governance";
type MarketScope = "ALL" | "SE" | "NO" | "DK";
export type OperatorState = "handled" | "needs_me" | "scheduled" | "auto";

interface GanttBarItem {
  id: string;
  track: "pushes" | "backlinks" | "content" | "governance";
  market: "ALL" | "SE" | "NO" | "DK";
  title: string;
  subtitle: string;
  startCol: number; // 1..20 (4 weeks per month across Nov, Dec, Jan, Feb, Mar)
  spanCols: number;
  defaultOperatorState: OperatorState;
  ownerLabel: "Dig (Kristoffer)" | "Auto / Antigravity";
  dueDateIso: string;
  dueDateLabel: string;
  orderedAtIso?: string;
  badge: string;
  commitOrRef?: string;
  deliverables: string[];
  targetUrls?: string[];
  spamGuardNote?: string;
}

const MONTHS = [
  { id: "nov26", label: "November 2026", sub: "Uge 45–48 · Fundament & Push 0–2", weeks: ["U45 (Nu)", "U46", "U47", "U48"] },
  { id: "dec26", label: "December 2026", sub: "Uge 49–52 · Push 3 & NO/DK Links", weeks: ["U49", "U50", "U51", "U52"] },
  { id: "jan27", label: "Januar 2027", sub: "Uge 1–4 · Højsæson & Räntebarometer", weeks: ["U1", "U2", "U3", "U4"] },
  { id: "feb27", label: "Februar 2027", sub: "Uge 5–8 · Plads 4–15 CTR Sprint", weeks: ["U5", "U6", "U7", "U8"] },
  { id: "mar27", label: "Marts 2027", sub: "Uge 9–13 · Top-5 Konsolidering", weeks: ["U9", "U10", "U11", "U12–13"] },
];

const GANTT_ITEMS: GanttBarItem[] = [
  // ─── SWIMLANE 1: KONTROLLEREDE PUSHES ──────────────────────────────────────
  {
    id: "push-0",
    track: "pushes",
    market: "ALL",
    title: "Push 0: Teknisk SEO, E-E-A-T & Redirect-redning",
    subtitle: "5.269 SE-eksp. reddet fra noindex · /jamfor-banker/ opgraderet · Redaktør-byline + JSON-LD",
    startCol: 1,
    spanCols: 1,
    defaultOperatorState: "handled",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-04",
    dueDateLabel: "4. nov 2026",
    badge: "Styr på det · Live 4. nov",
    commitOrRef: "commits 61f1050, fb96ebb, 9137f54, 0b75c35",
    deliverables: [
      "SE Redirect-redning: 5.269 eksponeringer og 21 klik fra gamle noindex-URL'er (/andra-lan/ m.fl.) 301-routet til /lana-pengar/, /samla-lan/ og /lan-med-betalningsanmarkning/.",
      "SE Striking Distance: /jamfor-banker/ (pos 11,9 på 'jämför lån bäst i test') udvidet med H1, 3 redaktionelle sektioner og 5 FAQs + FAQPage schema.",
      "SE Kannibalisering løst: /guide/samla-lan-bast-ranta/ (1.233 eksp.) konsolideret via 301 ind i /samla-lan/.",
      "Global E-E-A-T & Schema: Synlig redaktør-byline (Kristoffer Ryberg) + WebPage, Organization (Afima ApS, CVR DK-41412062), BreadcrumbList og nordisk hreflang på SE, NO, DK og FI."
    ],
    targetUrls: [
      "https://simplyloan.se/jamfor-banker/",
      "https://simplyloan.se/samla-lan/",
      "https://simplyloan.no/om-oss/",
      "https://simplyloan.dk/om-os/"
    ],
    spamGuardNote: "100 % teknisk & redaktionel kvalitetsopgradering — 0 nye tynde sider."
  },
  {
    id: "push-1",
    track: "pushes",
    market: "ALL",
    title: "Push 1: Billån & Renoveringslån Hubs (SE, NO, DK)",
    subtitle: "2 distinkte kommercielle produkt-kategorier pr. marked med egne lovkrav",
    startCol: 1,
    spanCols: 2,
    defaultOperatorState: "handled",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-04",
    dueDateLabel: "4. nov 2026",
    badge: "Styr på det · Live 4. nov",
    commitOrRef: "commit d11efc8",
    deliverables: [
      "Sverige: /billan/ (20 % kontantinsats vs. privatlån uden sikkerhed) og /renoveringslan/ (85 % belåningsgrad, amorteringskrav & ROT-avdrag).",
      "Norge: /lan-penger/billan-uten-sikkerhet/ og /lan-penger/lan-til-oppussing/ koblet på /lan-penger/ og norsk footer.",
      "Danmark: /renoveringslaan/ lanceret side om side med /billaan-beregner/ i dansk sitemap og interne link-targets (#153–#157)."
    ],
    targetUrls: [
      "https://simplyloan.se/billan/",
      "https://simplyloan.se/renoveringslan/",
      "https://simplyloan.no/lan-penger/billan-uten-sikkerhet/",
      "https://simplyloan.no/lan-penger/lan-til-oppussing/",
      "https://simplyloan.dk/renoveringslaan/"
    ],
    spamGuardNote: "Uge 46 holdes fri for nye struktur-pushes, så Google crawler Push 0 & 1 naturligt."
  },
  {
    id: "push-2",
    track: "pushes",
    market: "ALL",
    title: "Push 2: Interaktiv 'Samla Lån Besparings-kalkylator'",
    subtitle: "Før vs. Efter-modul på /samla-lan/, /refinansiering/ & /samlelaan/",
    startCol: 3,
    spanCols: 2,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-11-16",
    dueDateLabel: "Mandag 16. nov (Uge 47)",
    badge: "Planlagt Uge 47 (16. nov)",
    deliverables: [
      "Du får automatisk besked ved Deep Dive mandag d. 16. nov om at sætte Push 2 i gang.",
      "Interaktivt beregner-modul hvor brugeren indtaster nuværende smålån/kreditkort og straks ser månedlig besparelse + lavere effektiv rente.",
      "Øger Time-on-Site (dwell time) og konverteringsrate til Salus/Adtraction — uden at oprette nye URL'er (0 % doorway-risiko)."
    ],
    targetUrls: [
      "https://simplyloan.se/samla-lan/",
      "https://simplyloan.no/samle-lan/",
      "https://simplyloan.dk/samlelaan/"
    ],
    spamGuardNote: "Forbedrer eksisterende kerne-sider frem for at oprette nye sider."
  },
  {
    id: "push-3",
    track: "pushes",
    market: "ALL",
    title: "Push 3: Omstartslån / Samla lån med anmärkning + Bank-batch 2",
    subtitle: "Kryds-hub mellem samlingslån & betalningsanmärkning + 10 nye bank-reviews i køen",
    startCol: 5,
    spanCols: 3,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-11-30",
    dueDateLabel: "Mandag 30. nov (Uge 49)",
    badge: "Planlagt 30. nov (U49)",
    deliverables: [
      "Dedikeret kryds-side til høj-værdi segmentet: samle dyr gæld trods betalningsanmärkning / inkasso (med kautionist/medansøger eller sikkerhed).",
      "December-kalender fyldes med Batch 2 af specifikke bank-anmeldelser (Ikano Bank, Bank Norwegian, Santander, Morrow, Marginalen, Collector/Norion, Resurs)."
    ]
  },
  {
    id: "push-4",
    track: "pushes",
    market: "ALL",
    title: "Push 4: 'Ränteläget & Lånebarometer 2027' (Link-Bait Hub)",
    subtitle: "Datadrevet Q1 rente-rapport (Riksbanken / Norges Bank / Nationalbanken vs. 40 banker)",
    startCol: 9,
    spanCols: 4,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2027-01-11",
    dueDateLabel: "Mandag 11. jan 2027",
    badge: "Planlagt Jan 2027",
    deliverables: [
      "Autoritativ Q1 2027 dataside pr. marked med grafer over centralbankrente vs. faktisk udlånsrente på privatlån og samlelån.",
      "Designet specifikt som naturligt redaktionelt link-mål ('Link-Bait') til Q1 WhitePress-artikler i januar-højsæsonen."
    ]
  },
  {
    id: "push-5",
    track: "pushes",
    market: "ALL",
    title: "Push 5: 'Striking Distance' Sprint (Plads 4–15 → Top 3)",
    subtitle: "Målrettet CTR-, tabel- og FAQ-optimering af alle sider på plads 4–15 i GSC",
    startCol: 13,
    spanCols: 4,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2027-02-08",
    dueDateLabel: "Mandag 8. feb 2027",
    badge: "Planlagt Feb 2027",
    deliverables: [
      "Udtræk af alle søgeord i GSC, der efter januar-trafikken ligger på plads 4–15.",
      "Indlejring af skræddersyede sammenligningstabeller, opdaterede 2027-rentesatser og FAQ-sektioner for at løfte dem ind i Top 3."
    ]
  },
  {
    id: "push-6",
    track: "pushes",
    market: "ALL",
    title: "Push 6: Top-5 Money-Page Konsolidering & Q2 Klargøring",
    subtitle: "Intern PageRank-vægtning & CRO på de højest konverterende Salus/Adtraction-sider",
    startCol: 17,
    spanCols: 4,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2027-03-08",
    dueDateLabel: "Mandag 8. mar 2027",
    badge: "Planlagt Mar 2027",
    deliverables: [
      "Finjustering af interne link-ankre fra de ~150 udgivne guides direkte ind i top-konverterende money-pages.",
      "Klargøring af forårs-kampagnesider (renovering, bilkøb, samlelån før påske)."
    ]
  },

  // ─── SWIMLANE 2: BACKLINKS (SE, NO, DK) ────────────────────────────────────
  {
    id: "bl-se-nov",
    track: "backlinks",
    market: "SE",
    title: "🇸🇪 SE Nov: 3 WhitePress-links bestilt (#245719 · €765,91)",
    subtitle: "Bestilt 3. nov kl. 16:36 · enterprisemagazine.se · miniraknare.co · kronantillmiljonen.se",
    startCol: 1,
    spanCols: 4,
    defaultOperatorState: "handled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-11-03",
    dueDateLabel: "Bestilt 3. nov kl. 16:36",
    orderedAtIso: "2026-11-03T16:36:00+01:00",
    badge: "Styr på det · Bestilt 3. nov",
    commitOrRef: "WhitePress #245719 (3. nov kl. 16:36)",
    deliverables: [
      "1. kronantillmiljonen.se (€322,00 · Bestilt 3. nov kl. 16:36): 'Samla dyra lån och krediter – så sänker du månadskostnaden och blir skuldfri snabbare' → Peger på /samla-lan/ + /guide/samla-lan-och-krediter/",
      "2. miniraknare.co (€154,11 · Bestilt 3. nov kl. 16:36): 'Så räknar du ut effektiv ränta och månadskostnad på ett lån' → Peger på /guide/vad-ar-effektiv-ranta/",
      "3. enterprisemagazine.se (€289,80 · DR 68 · Bestilt 3. nov kl. 16:36): 'Så påverkar kreditvärdighet och ränteläge svenska hushålls och företagares finansiering' → Peger på https://simplyloan.se/ + /guide/kreditprovning-uc/",
      "Status fra din side: ✅ 100 % styr på det (betalt og bestilt). Udgiverne skriver og publicerer typisk inden for 2–5 hverdage."
    ],
    targetUrls: [
      "https://simplyloan.se/",
      "https://simplyloan.se/samla-lan/",
      "https://simplyloan.se/guide/kreditprovning-uc/",
      "https://simplyloan.se/guide/vad-ar-effektiv-ranta/",
      "https://simplyloan.se/guide/samla-lan-och-krediter/"
    ]
  },
  {
    id: "bl-se-dec-q1",
    track: "backlinks",
    market: "SE",
    title: "🇸🇪 SE Dec–Mar: Fast Link Velocity (2–3 links/md)",
    subtitle: "Næste bestilling: 7. dec mod /jamfor-banker/ & /billan/ · Jan–Mar: Räntebarometer & Plads 4–12",
    startCol: 5,
    spanCols: 16,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-12-07",
    dueDateLabel: "Mandag 7. dec 2026",
    badge: "Næste SE-køb: 7. dec",
    deliverables: [
      "Du får notifikation i klokken mandag d. 7. dec, når det er tid til decembers 2 svenske links (~2.500–3.500 DKK) mod /jamfor-banker/ (pos 11,9) og /billan/.",
      "Januar 2027: 2–3 links mod /samla-lan/, /lan-med-betalningsanmarkning/ og Räntebarometer 2027.",
      "Februar–Marts 2027: 2 links/md målrettet de sider, der ligger på plads 4–12 i GSC.",
      "Anker-regel: 50 % Branded/URL, 35 % Delvist match, maks. 15 % Eksakt match."
    ]
  },
  {
    id: "bl-no-nov",
    track: "backlinks",
    market: "NO",
    title: "🇳🇴 NO Nov (Plan A): Afventer til de 13 nye artikler er ude (23. nov)",
    subtitle: "Ingen handling nu — du får notifikation i klokken d. 23. nov når kalenderen er rullet ud",
    startCol: 1,
    spanCols: 3,
    defaultOperatorState: "handled",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-20",
    dueDateLabel: "Pause frem til 23. nov",
    badge: "Styr på det · Pause til 23. nov",
    deliverables: [
      "Aftalt 3. nov (Plan A): Vi holder bevidst igen med norske links i de første 3 uger af november, mens de 13 nye norske artikler drypper ud (4.–20. nov).",
      "Du skal IKKE gøre noget på NO lige nu — d. 23. nov vækker klokken dig automatisk til første norske link-indkøb."
    ]
  },
  {
    id: "bl-no-dec-q1",
    track: "backlinks",
    market: "NO",
    title: "🇳🇴 NO Opstart (23. nov) & Dec–Mar: 2–3 norske links → 2 links/md",
    subtitle: "Mål: simplyloan.no/, /samle-lan/, /lan-penger/ & /artikler/refinansiering/",
    startCol: 4,
    spanCols: 17,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-11-23",
    dueDateLabel: "Mandag 23. nov 2026",
    badge: "Notifikation 23. nov",
    deliverables: [
      "Mandag d. 23. nov 2026 (når de 13 norske artikler er live): Vi lægger de første 2–3 norske WhitePress-links (~2.500–3.500 DKK) i kurven mod simplyloan.no/, /samle-lan/ og /lan-penger/.",
      "Januar–Marts 2027: Stabil rytme på 2 norske links/md mod de stærkeste kommercielle sider i GSC."
    ],
    targetUrls: [
      "https://simplyloan.no/",
      "https://simplyloan.no/samle-lan/",
      "https://simplyloan.no/lan-penger/",
      "https://simplyloan.no/artikler/refinansiering/"
    ]
  },
  {
    id: "bl-dk-nov",
    track: "backlinks",
    market: "DK",
    title: "🇩🇰 DK Nov: Afventer indeksering af nye danske sider",
    subtitle: "Ingen handling i nov — 301-oprydning, E-E-A-T og 13 nov-artikler indekserer først",
    startCol: 1,
    spanCols: 4,
    defaultOperatorState: "handled",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-30",
    dueDateLabel: "Pause frem til 2. dec",
    badge: "Styr på det · Pause i nov",
    deliverables: [
      "Ingen link-udgift i november på DK: Vi lader de 5 nye partner-reviews (Sambla, Lendo, LendMe, Gældsfaktor, Låneevne) og /renoveringslaan/ indeksere naturligt først."
    ]
  },
  {
    id: "bl-dk-dec-q1",
    track: "backlinks",
    market: "DK",
    title: "🇩🇰 DK Opstart (2. dec) & Jan–Mar: 2–3 danske links → 2 links/md",
    subtitle: "Mål: simplyloan.dk/, /samlelaan/, /laan-penge/ & /renoveringslaan/",
    startCol: 5,
    spanCols: 16,
    defaultOperatorState: "scheduled",
    ownerLabel: "Dig (Kristoffer)",
    dueDateIso: "2026-12-02",
    dueDateLabel: "Onsdag 2. dec 2026",
    badge: "Notifikation 2. dec",
    deliverables: [
      "Onsdag d. 2. dec 2026: Du får notifikation i klokken til opstart af 2–3 danske finans-/nyhedslinks (~3.000 DKK) mod simplyloan.dk/, /samlelaan/ og /laan-penge/.",
      "Januar–Marts 2027: 2 danske links/md mod de søgeord, der viser bedst fremgang i GSC."
    ]
  },

  // ─── SWIMLANE 3: DAGLIG ARTIKEL- & ANMELDELSESKALENDER ─────────────────────
  {
    id: "cal-nov",
    track: "content",
    market: "ALL",
    title: "Nov Drip (1/dag/marked): Kerne-guides & Partner-anmeldelser Batch 1",
    subtitle: "21 SE · 16 NO · 13 DK · 14 FI planlagt i køen (min_buffer_days = 21)",
    startCol: 1,
    spanCols: 4,
    defaultOperatorState: "auto",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-04",
    dueDateLabel: "Kører 100 % automatisk",
    badge: "Auto 1/dag · Styr på det",
    deliverables: [
      "Udgiver maks. 1 artikel pr. marked pr. dag via smart_seo_publisher + automatisk Astro-rebuild.",
      "Indeholder dybdegående anmeldelser af vores direkte partnere (Sambla, Lendo, Axo Finans, Uno Finans, Digifinans, LendMe) + centrale regler (KALP, Gældsfaktor, 5x-inntekt, Effektiv rente)."
    ]
  },
  {
    id: "cal-dec",
    track: "content",
    market: "ALL",
    title: "Dec Drip (1/dag/marked): Tier-1 Bank-anmeldelser Batch 2 (~30/marked)",
    subtitle: "Ikano, Bank Norwegian, Santander, Morrow, Marginalen, Collector/Norion, Resurs, Coop m.fl.",
    startCol: 5,
    spanCols: 4,
    defaultOperatorState: "auto",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-12-01",
    dueDateLabel: "Auto-buffer (21 dage)",
    badge: "Auto-buffer (21 dage)",
    deliverables: [
      "Fokus på specifikke bank-anmeldelser ('[Bank] omdöme / erfaringer / ränta'), som har lav konkurrence i SERP og høj købsintention.",
      "Hver bank-anmeldelse linker internt til vores kommercielle sammenligningssider (/samla-lan/, /jamfor-banker/, /refinansiering/, /samlelaan/)."
    ]
  },
  {
    id: "cal-q1",
    track: "content",
    market: "ALL",
    title: "Q1 2027 Drip (Jan–Mar): ~90 Long-Tail & GSC-drevne artikler pr. marked",
    subtitle: "Styret af ugentlige GSC-data · Samlet ~150+ dybdegående sider pr. marked ved udgangen af Q1",
    startCol: 9,
    spanCols: 12,
    defaultOperatorState: "auto",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2027-01-01",
    dueDateLabel: "Auto 1/dag i Q1",
    badge: "1/dag · 0 % spam-risiko",
    deliverables: [
      "Kontinuerligt 1-om-dagen rul gennem januar, februar og marts.",
      "Emner vælges ud fra reelle søgetermer i Google Search Console fra vores ugentlige Deep Dives."
    ]
  },

  // ─── SWIMLANE 4: TEKNISK SEO, SOME ENTITY & UGENTLIGT DEEP DIVE ────────────
  {
    id: "gov-weekly",
    track: "governance",
    market: "ALL",
    title: "Fast rytme: Daglig SEO Puls (08:15) + Ugentligt Deep Dive (Mandag 09:00)",
    subtitle: "EventBridge → NotificationCenter i afima.ai + permanent historik i SEO_LOGBOOK.md",
    startCol: 1,
    spanCols: 20,
    defaultOperatorState: "auto",
    ownerLabel: "Auto / Antigravity",
    dueDateIso: "2026-11-09",
    dueDateLabel: "Hver mandag kl. 09:00",
    badge: "Aktiv i afima.ai",
    commitOrRef: "afima-seo-weekly-deep-dive & afima-seo-daily-digest",
    deliverables: [
      "Hver morgen kl. 04:00 & 07:00 UTC: Automatisk DataForSEO rank-tracking + Google Search Console synkronisering.",
      "Hver morgen kl. 08:15 CET: Daglig SEO Puls-kort i klokken på afima.ai (advarer automatisk hvis en Roadmap-opgave har nået sin dato uden at være markeret 'Styr på det').",
      "Hver mandag kl. 09:00 CET: Ugentligt SEO Deep Dive-kort i afima.ai med 1-klik 'Kopiér prompt til Antigravity' og logning i SEO_LOGBOOK.md.",
      "SoMe Entity: Officielle virksomhedsprofiler i Organization sameAs schema — 0 spildtimer på daglige organiske SoMe-opslag."
    ]
  }
];

interface BenchmarkRow {
  checkpoint: string;
  subLabel: string;
  isCurrent?: boolean;
  seTarget: string;
  noTarget: string;
  dkTarget: string;
  expectedSignal: string;
}

const BENCHMARKS: BenchmarkRow[] = [
  {
    checkpoint: "Baseline (4. Nov 2026)",
    subLabel: "Uge 45 · Startpunkt (renset for okt-bot)",
    isCurrent: true,
    seTarget: "14.761 eksp. / 29 klik (30d)",
    noTarget: "Opstarts-baseline (9 artikler live)",
    dkTarget: "Opstarts-baseline (6 artikler live)",
    expectedSignal: "Push 0 (Redirects + E-E-A-T) & Push 1 (Billån/Renovering) netop deployet. 3 SE WhitePress-links bestilt (3. nov kl. 16:36)."
  },
  {
    checkpoint: "1. December 2026",
    subLabel: "Om 4 uger · Slut Uge 48",
    seTarget: "25.000–35.000 eksp. / 80–150 klik/md",
    noTarget: "5.000–10.000 eksp. / 25–50 klik/md",
    dkTarget: "3.000–6.000 eksp. / 15–30 klik/md",
    expectedSignal: "De 3 svenske links (enterprisemagazine, miniraknare, kronantillmiljonen) er indekseret. /jamfor-banker/ rykker fra pos 11,9 ind i Top 6–8."
  },
  {
    checkpoint: "1. Januar 2027",
    subLabel: "Om 8 uger · Indgang til Q1 højsæson",
    seTarget: "50.000–75.000 eksp. / 250–450 klik/md",
    noTarget: "15.000–25.000 eksp. / 80–150 klik/md",
    dkTarget: "10.000–18.000 eksp. / 50–100 klik/md",
    expectedSignal: "~65 indekserede kvalitets-sider pr. marked. Norske og danske dec-links begynder at løfte /refinansiering/ og /samlelaan/."
  },
  {
    checkpoint: "1. Februar 2027",
    subLabel: "Om 12 uger · Efter januar-lånehøjsæson",
    seTarget: "90.000–130.000 eksp. / 600–1.000 klik/md",
    noTarget: "30.000–50.000 eksp. / 200–350 klik/md",
    dkTarget: "20.000–35.000 eksp. / 150–250 klik/md",
    expectedSignal: "Januar-effekten på samlelån slår igennem. Hovedsiderne bryder ind på Side 1–2 på kommercialiserede søgeord."
  },
  {
    checkpoint: "31. Marts 2027 (Slut Q1)",
    subLabel: "Om 20 uger · Fuldt udbygget autoritet",
    seTarget: "150.000+ eksp. / 1.200–2.000+ klik/md",
    noTarget: "60.000+ eksp. / 450–800 klik/md",
    dkTarget: "40.000+ eksp. / 300–600 klik/md",
    expectedSignal: "~150 dybdegående sider og 12–15 stærke backlinks pr. domæne. Stabil daglig organisk lead-strøm til Salus/Adtraction."
  }
];

function formatElapsedSince(isoString?: string): string {
  if (!isoString) return "";
  const orderedMs = new Date(isoString).getTime();
  const diffMs = Math.max(0, Date.now() - orderedMs);
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (totalHours < 48) {
    return `${totalHours} timer siden (3. nov kl. 16:36)`;
  }
  const days = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;
  return `${days} dage og ${remHours} timer siden (3. nov kl. 16:36)`;
}

export default function SeoRoadmapTab({
  market,
  overview,
  pusherState,
}: SeoRoadmapTabProps) {
  const [trackFilter, setTrackFilter] = useState<TrackFilter>("all");
  const [marketScope, setMarketScope] = useState<MarketScope>("ALL");
  const [selectedItemId, setSelectedItemId] = useState<string>("bl-se-nov");

  // Persistent operator "Styr på det" / "Mangler mig" state per Roadmap task
  const [operatorStates, setOperatorStates] = useState<Record<string, OperatorState>>(() => {
    const defaults: Record<string, OperatorState> = {};
    for (const item of GANTT_ITEMS) {
      defaults[item.id] = item.defaultOperatorState;
    }
    try {
      const saved = localStorage.getItem("simplyloan_seo_roadmap_states_v1");
      if (saved) {
        return { ...defaults, ...JSON.parse(saved) };
      }
    } catch {
      // ignore
    }
    return defaults;
  });

  useEffect(() => {
    try {
      localStorage.setItem("simplyloan_seo_roadmap_states_v1", JSON.stringify(operatorStates));
    } catch {
      // ignore
    }
  }, [operatorStates]);

  // Load persisted Roadmap states from PostgreSQL (admin.seo_roadmap_tasks)
  useEffect(() => {
    let mounted = true;
    seoApi
      .roadmap()
      .then((res) => {
        if (!mounted || !res?.items) return;
        const fromDb: Record<string, OperatorState> = {};
        for (const row of res.items) {
          if (row.status === "done") fromDb[row.task_id] = "handled";
          else if (row.status === "needs_you") fromDb[row.task_id] = "needs_me";
          else if (row.status === "auto") fromDb[row.task_id] = "auto";
          else if (row.status === "planned") fromDb[row.task_id] = "scheduled";
        }
        setOperatorStates((prev) => ({ ...prev, ...fromDb }));
      })
      .catch(() => {
        // Fallback to localStorage if offline
      });
    return () => {
      mounted = false;
    };
  }, []);

  const setItemOperatorState = (id: string, nextState: OperatorState) => {
    setOperatorStates((prev) => ({ ...prev, [id]: nextState }));
    const dbStatus =
      nextState === "handled"
        ? "done"
        : nextState === "needs_me"
        ? "needs_you"
        : nextState === "auto"
        ? "auto"
        : "planned";
    seoApi.updateRoadmapTask(id, dbStatus).catch(() => {
      // Saved locally in localStorage regardless
    });
  };

  // Compute which tasks actually require Kristoffer right now:
  // Either explicitly marked "needs_me", OR a "scheduled" task whose dueDateIso <= today!
  const todayIso = new Date().toISOString().slice(0, 10);

  const effectiveState = (item: GanttBarItem): OperatorState => {
    const st = operatorStates[item.id] || item.defaultOperatorState;
    if (st === "scheduled" && item.dueDateIso <= todayIso) {
      return "needs_me";
    }
    return st;
  };

  const tasksNeedingMeNow = useMemo(
    () => GANTT_ITEMS.filter((i) => effectiveState(i) === "needs_me"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operatorStates, todayIso]
  );

  const upcomingMyTasks = useMemo(
    () =>
      GANTT_ITEMS.filter((i) => i.ownerLabel === "Dig (Kristoffer)" && effectiveState(i) === "scheduled").sort((a, b) =>
        a.dueDateIso.localeCompare(b.dueDateIso)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operatorStates, todayIso]
  );

  const handledTasks = useMemo(
    () => GANTT_ITEMS.filter((i) => effectiveState(i) === "handled"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operatorStates, todayIso]
  );

  const filteredItems = GANTT_ITEMS.filter((item) => {
    if (trackFilter !== "all" && item.track !== trackFilter) return false;
    if (marketScope !== "ALL" && item.market !== "ALL" && item.market !== marketScope) return false;
    return true;
  });

  const selectedItem = GANTT_ITEMS.find((i) => i.id === selectedItemId) || GANTT_ITEMS[0];
  const selectedState = effectiveState(selectedItem);

  const barStyle = (st: OperatorState) => {
    switch (st) {
      case "handled":
        return "bg-emerald-600 text-white border-emerald-700 shadow-xs";
      case "needs_me":
        return "bg-amber-500 text-white border-amber-600 shadow-sm ring-2 ring-amber-300";
      case "auto":
        return "bg-[#0F3F34] text-white border-[#0A2B23] shadow-xs";
      case "scheduled":
      default:
        return "bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200/80";
    }
  };

  const barBadgeLabel = (item: GanttBarItem, st: OperatorState) => {
    if (st === "handled") return "✓ Styr på det";
    if (st === "needs_me") return "🔔 Mangler dig nu";
    if (st === "auto") return "🤖 Auto-drift";
    return item.badge;
  };

  const trackMeta: { id: GanttBarItem["track"]; title: string; subtitle: string; icon: React.ReactNode }[] = [
    {
      id: "pushes",
      title: "1. Kontrollerede 'Pushes' (Arkitektur & Hubs)",
      subtitle: "Maks. 1 strukturelt push pr. 1–2 uger = 0 % spam-risiko",
      icon: <Rocket className="w-4 h-4 text-[#0F3F34]" />,
    },
    {
      id: "backlinks",
      title: "2. Backlinks & Autoritet (SE, NO, DK)",
      subtitle: "WhitePress redaktionelle links · 50 % Brand / 35 % Partial / 15 % Exact",
      icon: <Link2 className="w-4 h-4 text-indigo-600" />,
    },
    {
      id: "content",
      title: "3. Daglig Artikel- & Anmeldelseskalender",
      subtitle: "Fast 1 artikel/dag pr. marked · Automatisk 21-dages buffer",
      icon: <Calendar className="w-4 h-4 text-emerald-600" />,
    },
    {
      id: "governance",
      title: "4. Teknisk SEO, SoMe Entity & Ugentligt Deep Dive",
      subtitle: "Daglig puls kl. 08:15 · Mandags Deep Dive kl. 09:00 · SEO_LOGBOOK.md",
      icon: <ShieldCheck className="w-4 h-4 text-amber-600" />,
    },
  ];

  const seNovElapsed = formatElapsedSince("2026-11-03T16:36:00+01:00");

  return (
    <div className="space-y-6">
      {/* ─── 1. DIN OPGAVELISTE LIGE NU (KRISTOFFER) ────────────────────────── */}
      <div
        className={`rounded-2xl p-6 border-2 shadow-sm transition-colors ${
          tasksNeedingMeNow.length === 0
            ? "bg-emerald-50/70 border-emerald-200"
            : "bg-amber-50/90 border-amber-300"
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div
              className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                tasksNeedingMeNow.length === 0
                  ? "bg-emerald-600 text-white"
                  : "bg-amber-500 text-white animate-pulse"
              }`}
            >
              {tasksNeedingMeNow.length === 0 ? (
                <UserCheck className="w-6 h-6" />
              ) : (
                <Bell className="w-6 h-6" />
              )}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                    tasksNeedingMeNow.length === 0
                      ? "bg-emerald-600 text-white"
                      : "bg-amber-600 text-white"
                  }`}
                >
                  {tasksNeedingMeNow.length === 0
                    ? "✓ Styr på alt lige nu"
                    : `🔔 ${tasksNeedingMeNow.length} opgave(r) tilfalder dig nu`}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  · Automatisk koblet til klokken i afima.ai (kl. 08:15 & mandag kl. 09:00)
                </span>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mt-1">
                {tasksNeedingMeNow.length === 0
                  ? "Ingen SEO-opgaver mangler dig i dag — alt for denne uge er vinget af!"
                  : "Følgende opgaver i Roadmap afventer din handling eller godkendelse:"}
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                {tasksNeedingMeNow.length === 0 ? (
                  <>
                    De 3 svenske WhitePress-links (<code>enterprisemagazine.se</code>, <code>miniraknare.co</code>,{" "}
                    <code>kronantillmiljonen.se</code>) blev bestilt for <strong>{seNovElapsed}</strong> — udgiverne
                    skriver/publicerer dem nu (typisk 2–5 hverdage). Næste gang en opgave rammer dit bord er{" "}
                    <strong>Mandag d. 9. nov kl. 09:00</strong> (Ugentligt Deep Dive) og{" "}
                    <strong>Mandag d. 16. nov</strong> (Push 2).
                  </>
                ) : (
                  "Klik '✓ Styr på det' ud for en opgave, når du har bestilt/godkendt den — så fjernes alarmen med det samme."
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="bg-white px-3.5 py-2 rounded-xl border border-gray-200 text-xs">
              <span className="text-gray-500 block">Styr på det ✅</span>
              <span className="font-bold text-emerald-700 text-sm">{handledTasks.length} punkter</span>
            </div>
            <div className="bg-white px-3.5 py-2 rounded-xl border border-gray-200 text-xs">
              <span className="text-gray-500 block">Mangler dig nu 🔔</span>
              <span className={`font-bold text-sm ${tasksNeedingMeNow.length > 0 ? "text-amber-600" : "text-gray-800"}`}>
                {tasksNeedingMeNow.length} punkter
              </span>
            </div>
            <div className="bg-white px-3.5 py-2 rounded-xl border border-gray-200 text-xs">
              <span className="text-gray-500 block">Kommende datoer 📅</span>
              <span className="font-bold text-gray-800 text-sm">{upcomingMyTasks.length} planlagt</span>
            </div>
          </div>
        </div>

        {/* If any tasks currently need Kristoffer, list them prominently with 1-click "✓ Styr på det" */}
        {tasksNeedingMeNow.length > 0 && (
          <div className="mt-4 pt-4 border-t border-amber-200/80 space-y-2">
            {tasksNeedingMeNow.map((t) => (
              <div
                key={t.id}
                className="bg-white p-3.5 rounded-xl border border-amber-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-bold text-[11px]">
                      Forfalden / Kræver dig ({t.dueDateLabel})
                    </span>
                    <span className="font-bold text-sm text-gray-900">{t.title}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{t.subtitle}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setSelectedItemId(t.id)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Se detaljer
                  </button>
                  <button
                    type="button"
                    onClick={() => setItemOperatorState(t.id, "handled")}
                    className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-2xs"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Marker "Styr på det"</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming tasks for Kristoffer (next 3 milestones with exact notification dates) */}
        <div className="mt-4 pt-4 border-t border-emerald-200/70 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white/90 rounded-xl p-3 border border-emerald-200/80 flex items-start justify-between gap-2">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                ✓ Styr på det · {seNovElapsed}
              </span>
              <div className="font-bold text-xs text-gray-900 mt-1">
                🇸🇪 3 WhitePress-links bestilt (#245719)
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                enterprisemagazine.se, miniraknare.co & kronantillmiljonen.se — udgivere skriver nu (2–5 hverdage).
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedItemId("bl-se-nov")}
              className="text-[11px] font-bold text-[#0F3F34] hover:underline shrink-0"
            >
              Detaljer →
            </button>
          </div>

          {upcomingMyTasks.slice(0, 2).map((u) => (
            <div
              key={u.id}
              className="bg-white/90 rounded-xl p-3 border border-gray-200 flex items-start justify-between gap-2"
            >
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                  📅 Næste opgave til dig: {u.dueDateLabel}
                </span>
                <div className="font-bold text-xs text-gray-900 mt-1">{u.title}</div>
                <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{u.subtitle}</div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedItemId(u.id)}
                className="text-[11px] font-bold text-[#0F3F34] hover:underline shrink-0"
              >
                Åbn →
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Filter Bar (Market Scope + Swimlane Filter) ─────────────────── */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold text-gray-400 uppercase mr-1 flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" /> Spor:
          </span>
          {[
            { id: "all", label: "Alle 4 spor" },
            { id: "pushes", label: "🚀 Pushes" },
            { id: "backlinks", label: "🔗 Backlinks" },
            { id: "content", label: "📅 Artikel-kalender" },
            { id: "governance", label: "🛠️ Teknisk & Deep Dive" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTrackFilter(t.id as TrackFilter)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                trackFilter === t.id
                  ? "bg-[#0F3F34] text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200/70"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold text-gray-400 uppercase mr-1">Landefilter:</span>
          {[
            { id: "ALL", label: "🌍 Alle (SE + NO + DK)" },
            { id: "SE", label: "🇸🇪 Kun Sverige" },
            { id: "NO", label: "🇳🇴 Kun Norge" },
            { id: "DK", label: "🇩🇰 Kun Danmark" },
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setMarketScope(m.id as MarketScope)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                marketScope === m.id
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200/70"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── SWIMLANE GANTT DIAGRAM (NOV 2026 – MAR 2027) ────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-gray-50/60">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-gray-900">
              Swimlane Gantt-Diagram (Uge 45, 2026 → Uge 13, 2027)
            </span>
            <span className="text-xs text-gray-500">
              — Klik på en bjælke for at se status eller markere "Styr på det"
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
              <span className="w-2.5 h-2.5 rounded-xs bg-emerald-600 inline-block" /> ✓ Styr på det (Færdig fra din side)
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
              <span className="w-2.5 h-2.5 rounded-xs bg-amber-500 inline-block" /> 🔔 Mangler dig nu
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
              <span className="w-2.5 h-2.5 rounded-xs bg-[#0F3F34] inline-block" /> 🤖 Auto-drift (1/dag & Puls)
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-600">
              <span className="w-2.5 h-2.5 rounded-xs bg-slate-200 border border-slate-300 inline-block" /> 📅 Planlagt (Notifikation når tiden er inde)
            </span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[980px]">
            {/* Month & Week Header Grid (20 columns) */}
            <div className="grid grid-cols-5 border-b border-gray-200 bg-gray-50">
              {MONTHS.map((m, idx) => (
                <div
                  key={m.id}
                  className={`p-3 ${idx < 4 ? "border-r border-gray-200" : ""} ${
                    idx === 0 ? "bg-emerald-50/50" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-gray-900 uppercase tracking-wider">{m.label}</span>
                    {idx === 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
                        I DAG (4. NOV)
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 truncate">{m.sub}</div>
                  <div className="grid grid-cols-4 gap-1 mt-2 pt-1.5 border-t border-gray-200/80">
                    {m.weeks.map((w, wIdx) => (
                      <span
                        key={w}
                        className={`text-[10px] text-center rounded py-0.5 font-semibold ${
                          idx === 0 && wIdx === 0
                            ? "bg-emerald-600 text-white font-bold"
                            : "text-gray-400 bg-white/80"
                        }`}
                      >
                        {w}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Swimlanes */}
            <div className="divide-y divide-gray-200 relative">
              {trackMeta
                .filter((tm) => trackFilter === "all" || tm.id === trackFilter)
                .map((tm) => {
                  const laneItems = filteredItems.filter((i) => i.track === tm.id);
                  return (
                    <div key={tm.id} className="p-4 space-y-2.5 bg-white">
                      {/* Swimlane Title */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {tm.icon}
                          <span className="font-bold text-xs text-gray-900 uppercase tracking-wide">
                            {tm.title}
                          </span>
                          <span className="text-xs text-gray-400 hidden sm:inline">· {tm.subtitle}</span>
                        </div>
                      </div>

                      {/* 20-Week Grid Rows for this Swimlane */}
                      <div className="space-y-2 pt-1">
                        {laneItems.map((item) => {
                          const isSelected = selectedItem?.id === item.id;
                          const st = effectiveState(item);
                          return (
                            <div
                              key={item.id}
                              className="grid grid-cols-20 gap-1 items-center relative"
                              style={{ gridTemplateColumns: "repeat(20, minmax(0, 1fr))" }}
                            >
                              <button
                                type="button"
                                onClick={() => setSelectedItemId(item.id)}
                                style={{
                                  gridColumnStart: item.startCol,
                                  gridColumnEnd: `span ${item.spanCols}`,
                                }}
                                className={`text-left px-3 py-2 rounded-xl border transition-all flex items-center justify-between gap-2 group ${barStyle(
                                  st
                                )} ${isSelected ? "ring-2 ring-offset-1 ring-[#0F3F34]" : ""}`}
                              >
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    {st === "handled" && (
                                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-emerald-200" />
                                    )}
                                    {st === "needs_me" && (
                                      <Bell className="w-3.5 h-3.5 shrink-0 text-white" />
                                    )}
                                    {st === "auto" && (
                                      <Clock className="w-3.5 h-3.5 shrink-0 text-emerald-300" />
                                    )}
                                    <span className="text-xs font-bold truncate block">{item.title}</span>
                                  </div>
                                  <div
                                    className={`text-[11px] truncate mt-0.5 ${
                                      st === "handled" || st === "needs_me" || st === "auto"
                                        ? "text-white/85"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {item.subtitle}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span
                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                      st === "handled" || st === "needs_me" || st === "auto"
                                        ? "bg-black/20 text-white"
                                        : "bg-white/90 text-gray-700 border border-gray-200"
                                    }`}
                                  >
                                    {barBadgeLabel(item, st)}
                                  </span>
                                  <ChevronRight className="w-3.5 h-3.5 opacity-60 group-hover:translate-x-0.5 transition-transform" />
                                </div>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* ─── SELECTED BAR DETAIL INSPECTOR + 1-CLICK "STYR PÅ DET" TOGGLE ─── */}
      {selectedItem && (
        <div className="bg-white rounded-2xl border-2 border-[#0F3F34]/20 p-6 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-gray-100">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                    selectedState === "handled"
                      ? "bg-emerald-100 text-emerald-800"
                      : selectedState === "needs_me"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-[#0F3F34]/10 text-[#0F3F34]"
                  }`}
                >
                  {barBadgeLabel(selectedItem, selectedState)}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Ansvarlig: {selectedItem.ownerLabel}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  Dato: {selectedItem.dueDateLabel}
                </span>
                {selectedItem.orderedAtIso && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-800">
                    ⏱️ Bestilt for {formatElapsedSince(selectedItem.orderedAtIso)}
                  </span>
                )}
                {selectedItem.commitOrRef && (
                  <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                    {selectedItem.commitOrRef}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mt-1.5">{selectedItem.title}</h3>
              <p className="text-sm text-gray-500">{selectedItem.subtitle}</p>
            </div>

            {/* 1-Click Operator Controls ("Styr på det" / "Mangler mig" / "Planlagt") */}
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {selectedState !== "handled" ? (
                <button
                  type="button"
                  onClick={() => setItemOperatorState(selectedItem.id, "handled")}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Marker "Styr på det" ✅</span>
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="px-3.5 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>Styr på det fra din side</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setItemOperatorState(selectedItem.id, "needs_me")}
                    className="px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold flex items-center gap-1"
                    title="Sæt tilbage på 'Mangler mig'-listen"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Sæt til "Mangler mig"</span>
                  </button>
                </div>
              )}

              {selectedState !== "needs_me" && selectedState !== "handled" && (
                <button
                  type="button"
                  onClick={() => setItemOperatorState(selectedItem.id, "needs_me")}
                  className="px-3 py-2 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-bold flex items-center gap-1.5"
                >
                  <Bell className="w-3.5 h-3.5" />
                  <span>Læg i "Mangler mig nu"</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-4">
            <div className="lg:col-span-2 space-y-2">
              <div className="text-xs font-bold text-gray-400 uppercase">Konkrete Leverancer & Detaljer</div>
              <ul className="space-y-2">
                {selectedItem.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-[#0F3F34] shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {selectedItem.targetUrls && selectedItem.targetUrls.length > 0 && (
              <div className="space-y-2 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="text-xs font-bold text-gray-400 uppercase">Mål-URL'er i Produktion</div>
                <div className="space-y-1.5">
                  {selectedItem.targetUrls.map((u) => (
                    <a
                      key={u}
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-2 text-xs font-mono text-[#0F3F34] hover:underline bg-white px-3 py-1.5 rounded-lg border border-gray-200"
                    >
                      <span className="truncate">{u.replace(/^https?:\/\//, "")}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── BENCHMARK SCORECARD (HVORNÅR SER VI REEL FREMGANG?) ─────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
              <Target className="w-4 h-4 text-[#0F3F34]" />
              <span>GSC Benchmarks & Milepæle: Hvornår bør vi se reel fremgang?</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              I finans/YMYL har Google 4–8 ugers forsinkelse fra tekniske fixes og backlinks til placeringerne rykker i
              Top 5.
            </p>
          </div>
          {overview && (
            <div className="px-3.5 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 font-semibold">
              Live GSC ({market}, 28d):{" "}
              <strong>{overview.gsc.impressions28d.toLocaleString("da-DK")} eksp.</strong> ·{" "}
              <strong>{overview.gsc.clicks28d.toLocaleString("da-DK")} klik</strong>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase border-b border-gray-100">
              <tr>
                <th className="py-3.5 px-5">Milepæl</th>
                <th className="py-3.5 px-4">🇸🇪 Sverige (`simplyloan.se`)</th>
                <th className="py-3.5 px-4">🇳🇴 Norge (`simplyloan.no`)</th>
                <th className="py-3.5 px-4">🇩🇰 Danmark (`simplyloan.dk`)</th>
                <th className="py-3.5 px-5">Hvad skal vi se i GSC?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {BENCHMARKS.map((b) => (
                <tr
                  key={b.checkpoint}
                  className={b.isCurrent ? "bg-emerald-50/40 font-medium" : "hover:bg-gray-50/60"}
                >
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">{b.checkpoint}</span>
                      {b.isCurrent && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                          Her er vi nu
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{b.subLabel}</div>
                  </td>
                  <td className="py-4 px-4 font-semibold text-gray-800">{b.seTarget}</td>
                  <td className="py-4 px-4 font-semibold text-gray-800">{b.noTarget}</td>
                  <td className="py-4 px-4 font-semibold text-gray-800">{b.dkTarget}</td>
                  <td className="py-4 px-5 text-xs text-gray-600 max-w-md">{b.expectedSignal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
